import React, { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faAngleLeft,
  faAngleRight,
  faSpinner,
  faFileInvoice,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { BASE_URL } from '../../config';

const InterestFeeGenerate = () => {
  const [filters, setFilters] = useState({
    corporate: [],
    billingFrom: '',
    billingTo: '',
  });

  const [corporateSearch, setCorporateSearch] = useState('');
  const [filteredCorporations, setFilteredCorporations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [billNumber, setBillNumber] = useState('');

  const phpFormatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const fetchCorporations = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/interest-fee/corporations`, {
        params: { search },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setFilteredCorporations(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch corporations.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCorporateSearch = (e) => {
    const value = e.target.value;
    setCorporateSearch(value);
    fetchCorporations(value);
  };

  const handleCorporateSelect = (corp) => {
    if (filters.corporate.some((c) => c.id === corp.id)) {
      toast.error(`"${corp.name}" is already selected.`, { duration: 3000 });
      setCorporateSearch('');
      setFilteredCorporations([]);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      corporate: [...prev.corporate, corp],
    }));
    setCorporateSearch('');
    setFilteredCorporations([]);
  };

  const handleRemoveCorporate = (corpId) => {
    setFilters((prev) => ({
      ...prev,
      corporate: prev.corporate.filter((c) => c.id !== corpId),
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedPayments([]);
    setSelectAll(false);
  };

  const handleCheckboxChange = (paymentId) => {
    setSelectedPayments((prev) => {
      if (prev.includes(paymentId)) {
        setSelectAll(false);
        return prev.filter((id) => id !== paymentId);
      }
      const newSelected = [...prev, paymentId];
      const allCurrentPageSelected = filteredPayments.every((payment) =>
        newSelected.includes(payment.id)
      );
      setSelectAll(allCurrentPageSelected && newSelected.length === totalEntries);
      return newSelected;
    });
  };

  const handleSelectAll = async () => {
    if (selectAll) {
      setSelectedPayments([]);
      setSelectAll(false);
    } else {
      try {
        setSelectAllLoading(true);
        const response = await axios.get(`${BASE_URL}/interest-fee/interest-eligible`, {
          params: {
            corporate: filters.corporate.map((c) => c.id),
            billing_from: filters.billingFrom,
            billing_to: filters.billingTo,
            page: 1,
            per_page: 100, // Fetch all for selection
          },
          headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
        });
        const allPaymentIds = response.data.data.map((payment) => payment.id);
        setSelectedPayments(allPaymentIds);
        setSelectAll(true);
        toast.success(`Selected all ${allPaymentIds.length} payments.`, { duration: 3000 });
      } catch (error) {
        toast.error('Failed to select all payments.', { duration: 3000 });
      } finally {
        setSelectAllLoading(false);
      }
    }
  };

  const handleGenerateBillingClick = () => {
    if (selectedPayments.length === 0) {
      toast.error('Please select at least one payment.', { duration: 3000 });
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmBilling = async () => {
    if (!billNumber || billNumber <= 0) {
      toast.error('Please enter a valid Statement of Account number.', { duration: 3000 });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/interest-fee/interest-generate`,
        {
          selected_payments: selectedPayments,
          bill_number: parseInt(billNumber),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` } }
      );
      toast.success(response.data.message, { duration: 3000 });
      setShowConfirmModal(false);
      setBillNumber('');
      setSelectedPayments([]);
      setSelectAll(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate billing.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/interest-fee/interest-eligible`, {
        params: {
          corporate: filters.corporate.map((c) => c.id),
          billing_from: filters.billingFrom,
          billing_to: filters.billingTo,
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setFilteredPayments(response.data.data);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Failed to fetch payments.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filteredPayments.length > 0) {
      const allCurrentPageSelected = filteredPayments.every((payment) => selectedPayments.includes(payment.id));
      setSelectAll(allCurrentPageSelected && selectedPayments.length === totalEntries);
    } else {
      setSelectAll(false);
    }
  }, [selectedPayments, filteredPayments, totalEntries]);

  useEffect(() => {
    fetchPayments();
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchCorporations();
  }, []);

  const columns = [
    {
      key: 'checkbox',
      label: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={handleSelectAll}
          disabled={selectAllLoading}
          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
        />
      ),
      sortable: false,
    },
    { key: 'seriesNo', label: 'Series No.', sortable: false },
    { key: 'corpCode', label: 'Corp Code', sortable: false },
    { key: 'corpName', label: 'Corp Name', sortable: false },
    { key: 'billingDate', label: 'Billing Date', sortable: false },
    { key: 'billedAmount', label: 'Billed Amount', sortable: false },
    { key: 'interestFee', label: 'Interest Fee', sortable: false },
    { key: 'netAmount', label: 'Net', sortable: false },
    { key: 'vatAmount', label: 'VAT', sortable: false },
    { key: 'grossAmount', label: 'Gross', sortable: false },
    { key: 'billedType', label: 'Fee Type', sortable: false },
  ];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} entries`;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 11 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b border-gray-100">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-2">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="max-w-1xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-600 text-white rounded-lg">
            <FontAwesomeIcon icon={faSearch} className="text-sm" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Interest Fee Generate</h1>
            <p className="text-xs text-gray-500">Generate interest fee billings</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1">Corporate</label>
                <input
                  type="text"
                  placeholder="Search corporate..."
                  value={corporateSearch}
                  onChange={handleCorporateSearch}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                />
                {/* {filteredCorporations.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                    {filteredCorporations.map((corp) => (
                      <li
                        key={corp.id}
                        onClick={() => handleCorporateSelect(corp)}
                        className="px-3 py-1.5 text-sm hover:bg-indigo-50 cursor-pointer"
                      >
                        {corp.id} - {corp.name}
                      </li>
                    ))}
                  </ul>
                )} */}
                {corporateSearch &&  filteredCorporations.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                    {filteredCorporations.map((corp) => (
                      <li
                        key={corp.id}
                        onClick={() => handleCorporateSelect(corp)}
                        className="px-3 py-1.5 text-sm hover:bg-indigo-50 cursor-pointer"
                      >
                        {corp.id} - {corp.name}
                      </li>
                    ))}
                  </ul>
                )}
                {filters.corporate.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {filters.corporate.map((corp) => (
                      <span
                        key={corp.id}
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                      >
                        {corp.name}
                        <button
                          onClick={() => handleRemoveCorporate(corp.id)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Billing From</label>
                  <input
                    type="date"
                    value={filters.billingFrom}
                    onChange={(e) => handleFilterChange('billingFrom', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Billing To</label>
                  <input
                    type="date"
                    value={filters.billingTo}
                    onChange={(e) => handleFilterChange('billingTo', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key} className="p-2 text-left font-semibold text-gray-600">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                  ) : filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-2 text-center text-gray-500">
                        No eligible payments found.
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment, index) => (
                      <tr key={payment.id} className="hover:bg-gray-50 transition">
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedPayments.includes(payment.id)}
                            onChange={() => handleCheckboxChange(payment.id)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </td>
                        {/* <td className="p-2">{(currentPage - 1) * itemsPerPage + index + 1}</td> */}
                        <td className="p-2">{payment.series_no}</td>
                        <td className="p-2">{payment.corp_code}</td>
                        <td className="p-2">{payment.corp_name}</td>
                        <td className="p-2">{payment.billing_date}</td>
                        <td className="p-2">{phpFormatter.format(payment.billed_amount)}</td>
                        <td className="p-2">{phpFormatter.format(payment.interest_fee)}</td>
                        <td className="p-2">{phpFormatter.format(payment.net_amount)}</td>
                        <td className="p-2">{phpFormatter.format(payment.vat_amount)}</td>
                        <td className="p-2">{phpFormatter.format(payment.gross_amount)}</td>
                        <td className="p-2">{payment.billed_type}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
           <div className="flex items-center gap-2 text-xs text-gray-600">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>{val}/page</option>
                ))}
              </select>
              <span>{getShowingEntriesText()}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-xs text-gray-600">{currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleGenerateBillingClick}
                disabled={selectedPayments.length === 0 || loading || selectAllLoading}
                className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 transition ${
                  selectedPayments.length === 0 || loading || selectAllLoading
                    ? 'bg-green-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <FontAwesomeIcon icon={faFileInvoice} /> Generate Billing
              </button>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-full max-w-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Confirm Billing</h2>
              <p className="text-xs text-gray-600 mb-3">
                Generate <span className="font-bold">Statement of Account</span> for{' '}
                <span className="font-bold">{selectedPayments.length}</span> payment(s).
              </p>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Statement of Account Number
              </label>
              <input
                type="number"
                value={billNumber}
                onChange={(e) => setBillNumber(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 mb-3"
                placeholder="Enter SOA number"
                min="1"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setBillNumber('');
                  }}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBilling}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 ${
                    loading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterestFeeGenerate;